"use client";

import { consentManager, type ConsentSettings } from "@/components/consent/consent-manager";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { CheckCircleIcon, Settings } from "lucide-react";
import { useEffect, useRef, useState } from "react";


export default function ConsentBanner() {
    const [showSheet, setShowSheet] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [settings, setSettings] = useState<ConsentSettings>({ analytics: false, chat: false });
    const acceptAllButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const initialConsent = consentManager.getInitialState();

        if (Object.values(initialConsent).some(value => value === undefined)) {
            setShowSheet(true);
        }
    }, []);

    const handleAccept = () => {
        setShowSheet(false);
        setShowDialog(false);
        consentManager.updateState({ analytics: true, chat: true });
    };

    const handleSavePreferences = () => {
        setShowDialog(false);
        setShowSheet(false);
        consentManager.updateState({ analytics: settings.analytics!, chat: settings.chat! });
    };

    const handleMandatoryOnly = () => {
        setShowSheet(false);
        consentManager.updateState({ analytics: false, chat: false });
    };

    const handleDialogClose = () => {
        setShowDialog(false);
        setShowSheet(true);
    };

    return (
        <>
            <Sheet
                open={showSheet}
                onOpenChange={() => { }}
            >
                <SheetContent
                    side="bottom"
                    className="mx-auto h-fit max-w-3xl bg-linear-to-br from-slate-950 to-slate-800 p-4 md:rounded-t-xl"
                    onInteractOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                    hideCloseButton
                    onOpenAutoFocus={(e) => {
                        e.preventDefault();
                        acceptAllButtonRef.current?.focus();
                    }}
                >
                    <SheetHeader className="text-left">
                        <SheetTitle className="text-slate-50">Cookie Einstellungen</SheetTitle>
                        <SheetDescription className="my-0 py-0 text-slate-50">
                            Wir verwenden Cookies für ein optimales Website-Erlebnis.
                            Wähle aus, welche optionalen Funktionen du nutzen möchtest.
                        </SheetDescription>
                    </SheetHeader>
                    <SheetFooter className="mx-auto mb-2 mt-2 max-w-64 flex-col items-center justify-between gap-2 sm:max-w-full sm:flex-row">
                        <Button
                            variant={"link"}
                            size={"sm"}
                            onClick={() => {
                                setShowDialog(true);
                                setShowSheet(false);
                            }}
                            className="flex-row text-amber-400"
                        >
                            <Settings className="mr-2 hidden h-4 w-4 sm:inline" />
                            Konfigurieren
                        </Button>

                        <Button
                            variant="link"
                            size={"sm"}
                            onClick={handleMandatoryOnly}
                            className="flex-row text-amber-400"
                        >
                            Nur Notwendige
                        </Button>

                        <Button
                            onClick={handleAccept}
                            ref={acceptAllButtonRef}
                            data-accept-all
                            variant={"default"}
                            className="flex-row"
                        >
                            <CheckCircleIcon className="mr-2 inline-block h-4 w-4" />
                            Alle akzeptieren
                        </Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>

            <Dialog
                open={showDialog}
                onOpenChange={() => { }}
            >
                <DialogContent
                    className="max-h-screen overflow-y-auto bg-slate-950/95 text-slate-50 sm:max-w-[500px]"
                    onInteractOutside={handleDialogClose}
                    onEscapeKeyDown={handleDialogClose}
                    hideCloseButton
                >
                    <DialogTitle className="text-xl font-bold">Cookie Einstellungen</DialogTitle>
                    <DialogDescription className="py-2" asChild>
                        <div>
                            <p className="mb-2">
                                Wir verwenden Cookies für ein optimales Website-Erlebnis.
                                Wähle aus, welche optionalen Funktionen du nutzen möchtest.
                            </p>
                        </div>
                    </DialogDescription>
                    <div className="space-y-4 pr-2">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="essential"
                                checked={true}
                                disabled
                            />
                            <div>
                                <Label htmlFor="essential" className="text-sm font-medium">
                                    Notwendige Cookies
                                </Label>
                                <p className="text-muted-foreground text-xs">
                                    Erforderlich für grundlegende Websitefunktionen wie Sitzungsverwaltung und Sicherheit.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="analytics"
                                className="border-amber-400/50 hover:border-amber-400"
                                checked={settings.analytics}
                                onCheckedChange={(checked) =>
                                    setSettings(prev => ({ ...prev, analytics: checked as boolean }))}
                            />
                            <div>
                                <Label htmlFor="analytics" className="text-sm font-medium">
                                    Analyse & Werbung
                                </Label>
                                <p className="text-muted-foreground text-xs">
                                    Ermöglicht uns die Messung und Optimierung unserer Website und Werbung. Umfasst Google Ads, Google Analytics sowie PostHog (Produktanalyse). Ohne Zustimmung erfolgt eine anonyme, cookielose Reichweitenmessung.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="chat"
                                className="border-amber-400/50 hover:border-amber-400"
                                checked={settings.chat}
                                onCheckedChange={(checked) =>
                                    setSettings(prev => ({ ...prev, chat: checked as boolean }))}
                            />
                            <div>
                                <Label htmlFor="chat" className="text-sm font-medium">
                                    Chat-Funktion
                                </Label>
                                <p className="text-muted-foreground text-xs">
                                    Speichert Ihre Chat-Einstellungen und Verlauf für eine bessere Nutzererfahrung.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col items-center gap-4 pt-4">
                        <Button
                            variant={"link"}
                            size={"sm"}
                            onClick={handleSavePreferences}
                            className="flex-row text-sm text-amber-400"
                        >
                            Auswahl bestätigen
                        </Button>

                        <Button
                            variant={"default"}
                            size={"lg"}
                            onClick={handleAccept}
                            className="h-16 flex-row text-center"
                        >
                            <CheckCircleIcon className="mr-2 h-4 w-4" />
                            Alle akzeptieren
                        </Button>
                    </div>
                    <p className="text-muted-foreground text-center text-xs">
                        Weitere Informationen findest du in unserer{" "}
                        <a href="/impressum" className="hover:text-primary underline">
                            Datenschutzerklärung
                        </a>
                    </p>
                </DialogContent>
            </Dialog>
        </>
    );
}